import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { Fragment, useCallback, useMemo } from "react";
import { PaginationContainer, PaginationItem } from "./styles";

interface PaginationProps {
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
}

const Pagination = ({
	currentPage,
	totalPages,
	onPageChange,
}: PaginationProps) => {
	const handlePrevious = useCallback(() => {
		if (currentPage > 1) {
			onPageChange(currentPage - 1);
		}
	}, [currentPage, onPageChange]);

	const handleNext = useCallback(() => {
		if (currentPage < totalPages) {
			onPageChange(currentPage + 1);
		}
	}, [currentPage, onPageChange, totalPages]);

	const pageNumbers = useMemo(() => {
		// The two gaps are told apart so that every entry can be its own key.
		const pages: (number | "gap-start" | "gap-end")[] = [1];

		if (currentPage > 3) pages.push("gap-start");

		const last = Math.min(totalPages, currentPage + 2);
		for (let page = Math.max(2, currentPage - 1); page < last; page++) {
			pages.push(page);
		}

		if (currentPage < totalPages - 2) pages.push("gap-end");

		if (totalPages > 1) pages.push(totalPages);

		return pages;
	}, [currentPage, totalPages]);

	if (totalPages <= 1) {
		return null;
	}

	return (
		<PaginationContainer>
			<PaginationItem onClick={handlePrevious} $disabled={currentPage === 1}>
				<ChevronLeft />
			</PaginationItem>

			{pageNumbers.map((page) => (
				<Fragment key={page}>
					{typeof page === "string" ? (
						<PaginationItem $isEllipsis>
							<MoreHorizontal />
						</PaginationItem>
					) : (
						<PaginationItem
							$isActive={page === currentPage}
							onClick={() => onPageChange(page)}
						>
							{page}
						</PaginationItem>
					)}
				</Fragment>
			))}
			<PaginationItem
				onClick={handleNext}
				$disabled={currentPage === totalPages}
			>
				<ChevronRight />
			</PaginationItem>
		</PaginationContainer>
	);
};

export default Pagination;
